/* eslint-disable css-modules/no-unused-class */
import { ClipboardEvent, KeyboardEvent, FocusEvent, ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Design System
import { Message } from 'checkout-forms/components/message/Message';
import { TextArea } from 'jl-design-system/components/text-area/TextArea';

// Types
import { AppDispatch, RootState } from 'types/RootState.types';
import { GiftMessageProps } from './GiftMessage.types';

// Config
import giftMessageState from './GiftMessage.state';
import { setDefaultGiftMessageValue } from '@redux/actions/app/appActions';
import {
  getTextAreaLinesArray,
  isAscii,
  getNextCharsInfo,
  getCharsLeft,
  calcLinesAfterEdit,
  replaceNonAsciiExtended,
  parseCharCode,
} from '@utils/string/textAreaUtils';

// Components
import BodyText from '../body-text';
import Container from '../container';

// Styles
import styles from './gift-message.scss';

const GiftMessage = ({
  ariaDescribedBy,
  classNameOverride,
  describedById,
  giftFormId = '',
  id,
  label,
  maxChars = 0,
  maxCharsLine = 0,
  maxLines = 0,
  name,
  onBlur = () => {},
  onChange = () => {},
  onFocus = () => {},
  value = '',
}: GiftMessageProps) => {
  const dispatch: AppDispatch = useDispatch();
  const { defaultGiftMessageValue = '', giftMessageValidationFailed } = useSelector((state: RootState) =>
    giftMessageState(state, giftFormId),
  );

  const textAreaWrapperRef = useRef<HTMLDivElement>(null);
  const [textAreaElement, setTextAreaElement] = useState<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (textAreaWrapperRef.current) {
      setTextAreaElement(textAreaWrapperRef.current.querySelector('textarea'));
    }
  }, [textAreaWrapperRef]);

  const [charsLeft, setCharsLeft] = useState<number>(maxChars);
  const [linesLeft, setLinesLeft] = useState<number>(maxLines);
  const [textAreaValue, setTextAreaValue] = useState<string>(defaultGiftMessageValue);

  useEffect(() => {
    if (textAreaElement) {
      const linesArray = getTextAreaLinesArray({
        string: value,
        maxLines,
        maxCharsLine,
      });

      const initCharsLeft = getCharsLeft({
        linesArray,
        maxLines,
        maxCharsLine,
      });

      setCharsLeft(initCharsLeft);
      setLinesLeft(maxLines - linesArray.length);
    }
  }, [textAreaElement, value, maxLines, maxCharsLine, maxChars]);

  const onKeyDownHandler = (evt: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!textAreaElement) return;

    const charCode = evt.key.toLowerCase();

    // IME composition (e.g. CJK input) reports evt.key as "Process"/"Unidentified" rather than the
    // real character being composed - skip the per-keystroke checks and let calcStats() correct the
    // result afterwards once the composed text actually lands via the change event
    const isComposing = evt.nativeEvent?.isComposing || charCode === 'process' || charCode === 'unidentified';

    if (isComposing) return;

    if (!charCode.includes('arrow')) {
      const isBackspace = charCode === 'backspace' || charCode === 'delete';
      const isTab = charCode === 'tab';
      const { prevChar, nextChar, newLine } = getNextCharsInfo(textAreaElement);
      const doNotAllowSpace = prevChar === ' ' || nextChar === ' ' || newLine;
      const isNotAscii = !isAscii({ value: charCode, extended: true });
      const insert = parseCharCode(charCode);

      const linesAfterEdit = calcLinesAfterEdit({
        textArea: textAreaElement,
        insert,
        maxCharsLine,
        maxLines,
        isTextArea: true,
      });
      const willExceedMaxLines = linesAfterEdit > maxLines;

      if (
        (!charsLeft && !isBackspace && !isTab) ||
        isNotAscii ||
        (charCode === ' ' && doNotAllowSpace) ||
        willExceedMaxLines
      ) {
        evt.preventDefault();
      }
    }
  };

  const onKeyUpHandler = (evt: KeyboardEvent<HTMLTextAreaElement>) => {
    const charCode = evt.key.toLowerCase();

    if (charCode.includes('arrow')) {
      calcStats();
    }
  };

  const normaliseSmartQuotes = (value: string) => value.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
  const onChangeHandler = (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!textAreaElement) return;

    textAreaElement.value = replaceNonAsciiExtended(normaliseSmartQuotes(textAreaElement.value));

    onChange(evt);
    calcStats();
    dispatch(setDefaultGiftMessageValue({ value: textAreaElement.value, giftFormId }));
  };

  const onFocusHandler = (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFocus(evt);
    calcStats();
  };

  const onBlurHandler = (evt: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onBlur(evt);
    calcStats();
  };

  const onPasteHandler = (evt: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!textAreaElement) return;

    // allow the paste through; calcStats() truncates any excess lines/chars on the following change event
    const clipboardData = replaceNonAsciiExtended(evt.clipboardData.getData('Text'));

    evt.clipboardData.setData('Text', clipboardData);
  };

  const getRemainingCharacters = (linesArray: any) => {
    if (!textAreaElement) return 0;

    return getCharsLeft({
      linesArray,
      maxLines,
      maxCharsLine,
    });
  };

  const calcStats = () => {
    if (!textAreaElement) return;
    setTextAreaValue(textAreaElement.value);

    let linesArray = getTextAreaLinesArray({
      string: textAreaElement.value,
      maxLines,
      maxCharsLine,
    });
    let charsLeft = getRemainingCharacters(linesArray);

    // Handle negative values because of iOS https://www.jlpit.com/jira/browse/MARV-4403
    const charsLeftIsNegative = charsLeft && charsLeft / Math.abs(charsLeft) < 0;
    if (charsLeftIsNegative) {
      // trim the characters that have gone over the limit and set the textarea value
      textAreaElement.value = textAreaElement.value.substring(Math.abs(charsLeft), textAreaElement.value.length).trim();
      setTextAreaValue(textAreaElement.value);

      // recalculate lines and characters left after truncating
      linesArray = getTextAreaLinesArray({
        string: textAreaElement.value,
        maxLines,
        maxCharsLine,
      });
      charsLeft = getRemainingCharacters(linesArray);
    }

    // raw line count/length can exceed the limits even when linesArray doesn't, since getTextAreaLinesArray
    // silently drops excess raw lines and trims wrap-only trailing spaces rather than reporting the raw overflow
    // (e.g. dropped/pasted discrete lines, or a single un-broken line longer than maxCharsLine)
    const rawSegments = textAreaElement.value.split(/\r\n|\r|\n/);
    const hasLineOverMaxChars = rawSegments.some((segment) => segment.length > maxCharsLine);
    const hasExcessLines = linesArray.length > maxLines || rawSegments.length > maxLines || hasLineOverMaxChars;

    if (hasExcessLines) {
      // hard-wrap/drop lines beyond the limits so what's displayed always matches what gets submitted
      textAreaElement.value = linesArray.slice(0, maxLines).join('\n');
      setTextAreaValue(textAreaElement.value);

      linesArray = getTextAreaLinesArray({
        string: textAreaElement.value,
        maxLines,
        maxCharsLine,
      });
      charsLeft = getRemainingCharacters(linesArray);
    }

    setCharsLeft(Math.max(0, charsLeft));
    setLinesLeft(Math.max(0, maxLines - linesArray.length));
  };

  const role = 'text';

  return (
    <div className={classNameOverride}>
      {giftMessageValidationFailed && (
        <Container marginBottom="3">
          <Message
            body="Please check your gift message for extra characters or lines and try again."
            dataTestId="gift-message-validation-failed-error"
            title="Sorry, something went wrong"
            type="error"
          />
        </Container>
      )}

      <div ref={textAreaWrapperRef} className={styles.messageContainer}>
        <Container marginBottom="1">
          <TextArea
            ariaDescribedBy={ariaDescribedBy}
            id={id}
            label={label}
            maxLength={(maxChars || 0) + (maxLines - 1)}
            name={name}
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            onClick={onFocusHandler}
            onFocus={onFocusHandler}
            onKeyDown={onKeyDownHandler}
            onKeyUp={onKeyUpHandler}
            onPaste={onPasteHandler}
            showLabel={false}
            value={textAreaValue}
          />
        </Container>
        <div id={describedById}>
          <BodyText marginBottom="1" role={role}>
            <strong>{charsLeft}</strong> characters and <strong>{linesLeft}</strong>{' '}
            {linesLeft === 1 ? 'line' : 'lines'} remaining
          </BodyText>
        </div>
      </div>
    </div>
  );
};

export default GiftMessage;
